<template>
  <panel :title="title" noButton="true" v-has-perms="rolePerm" class="task-edit-panel">
    <div class="task-edit">
      <b-form>
        <b-form-row v-if="showOrganizationAutocomplete">
          <form-thux-horizontal-autocomplete
            :label-form="'Organization' | translate"
            class-form="col-lg-6 mb-2"
            labelColsSm="3"
            labelColsLg="3"
            label-search="name"
            :is-required="true"
            :disabled="!formEditable"
            :value="organizationAutocompleteValue"
            :options="organizationOptionsMulti"
            :placeholder="$t('Type to search')"
            :min-length="0"
            :internal-search="false"
            @search="onSearchOrganization"
            @select="onSelectOrganization"
            @clear="onClearOrganization"
          />
        </b-form-row>
        <b-form-row v-else-if="organizationDisplay">
          <form-thux-horizontal-input
            :label-form="'Organization' | translate"
            class-form="col-lg-6 mb-2"
            labelColsSm="3"
            labelColsLg="3"
            type="text"
            :value="organizationDisplay"
            :disabled="true"
            :is-required="false"
            @change="() => {}"
          />
        </b-form-row>

        <b-form-row>
          <form-thux-horizontal-select
            :validator="$v.form.category"
            :external-errors="errors['category']"
            :label-form="'Category' | translate"
            class-form="col-lg-4 mb-2 triple-row"
            labelColsSm="4"
            labelColsLg="4"
            label-search="text"
            :disabled="!formEditable"
            :value="macroSelectValue"
            :placeholder="$t('Select an option')"
            :options="macroOptions"
            @select="onSelectMacro"
            @clear="onClearMacro"
          />
          <task-priority-select
            :validator="$v.form.priority"
            :external-errors="errors['priority']"
            :label-form="'Priority' | translate"
            class-form="col-lg-4 mb-2 triple-row"
            labelColsSm="4"
            labelColsLg="4"
            :disabled="!formEditable"
            :value="prioritySelectValue"
            :placeholder="$t('Select an option')"
            :options="taskPriorityOptions"
            @select="onSelectPriority"
            @clear="onClearPriority"
          />
          <task-state-select
            :external-errors="errors['state']"
            :label-form="'State' | translate"
            class-form="col-lg-4 mb-2 triple-row"
            labelColsSm="4"
            labelColsLg="4"
            :disabled="!formEditable"
            :value="stateSelectValue"
            :placeholder="$t('Select an option')"
            :options="stateOptions"
            @select="onSelectState"
            @clear="onClearState"
          />
        </b-form-row>

        <b-form-row>
          <form-thux-horizontal-input
            :validator="$v.form.name"
            :external-errors="errors['name']"
            :label-form="'Name' | translate"
            class-form="col-lg-12 mb-2 description-row"
            labelColsSm="3"
            labelColsLg="3"
            type="text"
            :value="form.name"
            :disabled="!formEditable"
            @change="$set(form, 'name', $event)"
          />
        </b-form-row>

        <b-form-row>
          <form-thux-horizontal-text-area
            :validator="$v.form.description"
            :external-errors="errors['description']"
            :label-form="'Description' | translate"
            class-form="col-lg-12 mb-2 description-row"
            labelColsSm="3"
            labelColsLg="3"
            :rows="3"
            :value="form.description"
            :disabled="!formEditable"
            @change="$set(form, 'description', $event)"
          />
        </b-form-row>

        <b-form-row>
          <form-thux-horizontal-multiselect
            :validator="$v.form.assignees"
            :external-errors="errors['assignees']"
            :label-form="'Assignees' | translate"
            class-form="col-lg-6 mb-2"
            labelColsSm="3"
            labelColsLg="3"
            label-search="name"
            :disabled="!formEditable"
            :value="assigneesMulti"
            :options="assigneeOptionsMulti"
            :placeholder="$t('Type to search')"
            :min-length="0"
            :internal-search="false"
            @search="onSearchAssignee"
            @select="onSelectAssignee"
            @remove="onRemoveAssignee"
            @clear="onClearAssignees"
          />
          <form-thux-horizontal-datetime
            :external-errors="errors['due_date']"
            :label-form="'Due date' | translate"
            class-form="col-lg-6 mb-2"
            labelColsSm="3"
            labelColsLg="3"
            type="date"
            :is-required="false"
            :disabled="!formEditable"
            :value="dueDate"
            :placeholder="$t('Select a date')"
            @change="onDueDateChange"
          />
        </b-form-row>

        <b-form-row>
          <form-thux-horizontal-multiselect
            :label-form="'Tags' | translate"
            class-form="col-lg-6 mb-2"
            labelColsSm="3"
            labelColsLg="3"
            label-search="name"
            :is-required="false"
            :disabled="!formEditable"
            :value="tagsMulti"
            :options="tagOptionsMulti"
            :placeholder="$t('Type to search')"
            :min-length="0"
            :taggable="formEditable"
            :tag-placeholder="$t('Press enter to create the tag')"
            @search="onSearchTag"
            @select="onSelectTag"
            @tag="onAddTag"
            @remove="onRemoveTag"
            @clear="onClearTags"
          />
          <form-thux-horizontal-duration
            :external-errors="errors['time_spent']"
            :label-form="'Time spent' | translate"
            class-form="col-lg-6 mb-2"
            labelColsSm="3"
            labelColsLg="3"
            :disabled="!formEditable"
            :value="form.time_spent"
            @change="$set(form, 'time_spent', $event)"
          />
        </b-form-row>

        <b-form-row v-if="editParams && editParams.totalIncome != null && editParams.totalIncome !== ''">
          <form-thux-horizontal-input
            :label-form="'Income' | translate"
            class-form="col-lg-6 mb-2"
            labelColsSm="3"
            labelColsLg="3"
            type="text"
            :value="editParams.totalIncome"
            :disabled="true"
            :is-required="false"
            @change="() => {}"
          />
        </b-form-row>
        <b-form-row v-if="!hideRelations">
          <form-thux-horizontal-autocomplete
            :label-form="'Related quotation' | translate"
            class-form="col-lg-6 mb-2"
            labelColsSm="3"
            labelColsLg="3"
            label-search="name"
            :is-required="false"
            :disabled="!formEditable || !form.organization"
            :value="quotationAutocompleteValue"
            :options="quotationOptionsMulti"
            :placeholder="quotationPlaceholder"
            :min-length="0"
            :internal-search="false"
            @search="onSearchQuotation"
            @select="onSelectQuotation"
            @clear="onClearQuotation"
          />
        </b-form-row>

      </b-form>

      <div v-if="creationInfo" class="mt-2 text-muted small">
        {{ creationInfo }}
      </div>

      <!-- ===== Sottoattività ===== -->
      <div v-if="canAddSubtasks" class="task-subtasks">
        <b-button
          variant="outline-success"
          class="task-add-subtask"
          :disabled="$v.form.$invalid"
          @click.prevent="addSubtask"
        >
          <i class="fas fa-plus mr-2"></i>{{ 'Add subtask' | translate }}
        </b-button>

        <div
          v-for="(sub, index) in subtasks"
          :key="sub._key"
          class="task-subtask-card"
        >
          <div class="task-subtask-card__head">
            <span class="f-w-600">
              <i class="fas fa-sitemap mr-2"></i>{{ 'Subtask' | translate }} {{ index + 1 }}
            </span>
            <b-button
              size="sm"
              variant="link"
              class="task-subtask-card__remove p-0"
              @click.prevent="removeSubtask(index)"
            >
              <i class="fas fa-times mr-1"></i>{{ 'Remove' | translate }}
            </b-button>
          </div>
          <div class="task-subtask-card__body">
            <task-subtask-form
              ref="subtaskForms"
              :edit-params="editParams"
              :form-editable="formEditable"
              :hide-relations="hideRelations"
              :initial-values="subtaskInitialValues"
              @validity-change="onSubtaskValidity(sub._key, $event)"
            />
          </div>
        </div>
      </div>

      <div v-if="parentInfo" class="parent-box mt-3">
        <div class="parent-box__head">
          <span class="parent-box__eyebrow">
            <i class="fas fa-sitemap mr-1"></i>{{ 'Parent task' | translate }}
          </span>
        </div>
        <b-row no-gutters class="parent-box__grid">
          <b-col cols="6" md="4" class="parent-box__cell">
            <div class="parent-box__label">{{ 'Name' | translate }}</div>
            <div class="parent-box__value">{{ parentInfo.name || '—' }}</div>
          </b-col>
          <b-col cols="6" md="4" class="parent-box__cell">
            <div class="parent-box__label">{{ 'Created by' | translate }}</div>
            <div class="parent-box__value">{{ parentInfo.creatorName || '—' }}</div>
          </b-col>
          <b-col cols="6" md="4" class="parent-box__cell">
            <div class="parent-box__label">{{ 'Creation date' | translate }}</div>
            <div class="parent-box__value">{{ parentInfo.date || '—' }}</div>
          </b-col>
          <b-col cols="12" class="parent-box__cell">
            <div class="parent-box__label">{{ 'Description' | translate }}</div>
            <div class="parent-box__value parent-box__value--desc">{{ parentInfo.description || '—' }}</div>
          </b-col>
        </b-row>
      </div>

      <div class="mt-3 f-w-600 d-flex justify-content-end">
        <b-button
          v-if="!readOnly && id && formEditable === false && getStateCode(form.state) !== 'done'"
          class="mr-3"
          variant="success"
          v-has-perms="editRolePerm"
          @click.prevent="onMarkDone()"
        >
          <i class="fas fa-check mr-2"></i>
          {{ 'Mark as done' | translate }}
        </b-button>
        <b-button
          class="mr-3"
          variant="default"
          @click.prevent="formEditable ? cancel() : closeForm()">
          {{ (formEditable ? 'Cancel' : 'Close') | translate }}
        </b-button>
        <b-button
          v-if="!readOnly"
          variant="primary"
          v-has-perms="editRolePerm"
          @click.prevent="formEditable ? onSubmit() : makeFormEditable()"
          :disabled="$v.form.$invalid || !allSubtasksValid">
          {{ (formEditable ? 'Save' : 'Edit') | translate }}
        </b-button>
      </div>
    </div>
    <task-terminate-modal
      v-if="showTerminateModal"
      :task="terminateModalTask"
      @terminated="onTerminateModalDone"
      @close="onTerminateModalClosed"
    />
    <task-open-subtasks-modal
      v-if="showOpenSubtasksModal"
      :task-id="id"
      @subtask-closed="onSubtaskClosed"
      @all-subtasks-closed="onOpenSubtasksAllClosed"
      @close="showOpenSubtasksModal = false"
    />
  </panel>
</template>

<script>
import { required } from 'vuelidate/lib/validators'
import { mapActions, mapGetters } from 'vuex'
import moment from 'moment'
import slugify from 'slugify'

import ThuxDetailPanelMixin from '@/components/thux-detail-panel/ThuxDetailPanelMixin'
import TaskFormMixin from './mixins/TaskFormMixin.js'
import TaskPrioritySelect from './components/TaskPrioritySelect.vue'
import TaskStateSelect from './components/TaskStateSelect.vue'
import TaskSubtaskForm from './components/TaskSubtaskForm.vue'
import TaskTerminateModal from './components/TaskTerminateModal.vue'
import TaskOpenSubtasksModal from './components/TaskOpenSubtasksModal.vue'
import { TYPES as TASK_TYPES } from './store.js'

export default {
  name: 'TaskEdit',
  mixins: [ThuxDetailPanelMixin, TaskFormMixin],
  components: {
    'task-priority-select': TaskPrioritySelect,
    'task-state-select': TaskStateSelect,
    'task-subtask-form': TaskSubtaskForm,
    'task-terminate-modal': TaskTerminateModal,
    'task-open-subtasks-modal': TaskOpenSubtasksModal
  },
  props: {
    hideRelations: { type: Boolean, default: false },
    readOnly: { type: Boolean, default: false },
    detailParams: { type: Object, default: () => ({}) }
  },
  data () {
    return {
      pageName: 'Task',
      rolePerm: 'tasks_task_list',
      editRolePerm: 'tasks_task_update',
      dueDate: undefined,
      organizationList: [],
      subtasks: [],
      subtaskValidity: {},
      showTerminateModal: false,
      showOpenSubtasksModal: false
    }
  },
  computed: {
    ...mapGetters({
      ...TASK_TYPES.GENERIC.task.task.DETAIL.GETTERS
    }),
    creationInfo () {
      // Mostrata solo in modifica (id valorizzato → è stata chiamata la retrieve).
      if (!this.id || !this.form || !this.form.date_create) return ''
      const creator = this.form.creator_name || ''
      const date = this.$options.filters.date(this.form.date_create)
      return this.$t('Activity created by {creator} on {date}', { creator, date })
    },
    canAddSubtasks () {
      // Il blocco "Aggiungi sottoattività" vive solo nella form in modifica di
      // un'attività di primo livello: sulle sotto-attività resta nascosto,
      // coerentemente col pulsante "Crea sottoattività" delle tabelle
      // (v-if="!item.parent").
      if (!this.formEditable || this.readOnly) return false
      return !(this.form && this.form.parent)
    },
    allSubtasksValid () {
      return this.subtasks.every((s) => this.subtaskValidity[s._key] === true)
    },
    subtaskInitialValues () {
      // Snapshot dei campi del task padre usato per inizializzare ogni nuova
      // sottoattività: Azienda, Categoria, Priorità, Stato, Incaricati, Data scadenza.
      return {
        organization: this.form.organization,
        organization_name: this.form.organization_name,
        category: this.form.category,
        category_name: this.form.category_name,
        priority: this.form.priority,
        priority_name: this.form.priority_name,
        state: this.form.state,
        assignees: this.ensureArray(this.form.assignees).slice(),
        assignees_detail: this.ensureArray(this.form.assignees_detail).slice(),
        due_date: this.form.due_date,
        dueDate: this.dueDate,
        quotation: this.form.quotation,
        quotation_detail: this.form.quotation_detail
      }
    },
    parentInfo () {
      // Riquadro col dettaglio del task padre: mostrato in tutti i sottotask
      // (ogni task che ha un padre), a prescindere dall'assegnazione.
      if (!this.id || !this.form || !this.form.parent_detail) return null
      const detail = this.form.parent_detail
      const dateFilter = this.$options.filters.date
      return {
        name: detail.name || '',
        description: detail.description || '',
        creatorName: detail.creator_name || '',
        date: detail.date_create ? dateFilter(detail.date_create) : ''
      }
    },
    title () {
      if (this.detailParams && this.detailParams.isSubtaskCreation) {
        const name = (this.detailParams.item && this.detailParams.item.name) || ''
        return `${this.$t('Add subtask')}${name ? ` - ${name}` : ''}`
      }
      if (this.id) {
        return this.formEditable
          ? `${this.$t('Edit')} ${this.$t(this.pageName).toLowerCase()}`
          : `${this.$t('Show')} ${this.$t(this.pageName).toLowerCase()}`
      }
      return `${this.$t('Add')} ${this.$t(this.pageName).toLowerCase()}`
    },
    organizationDisplay () {
      if (this.form && this.form.organization_name) {
        return this.form.organization_name
      }
      if (this.editParams && this.editParams.organizationName) {
        return this.editParams.organizationName
      }
      return null
    },
    categoryTypeCode () {
      return (this.editParams && this.editParams.categoryTypeCode) ||
        (this.detailParams && this.detailParams.categoryTypeCode) ||
        'company'
    },
    showOrganizationAutocomplete () {
      if (this.editParams && this.editParams.organization) return false
      return this.categoryTypeCode === 'customer'
    },
    organizationOptionsMulti () {
      return (this.organizationList || []).map((item) => ({ id: item.id, name: item.name }))
    },
    organizationAutocompleteValue () {
      if (!this.form || !this.form.organization) return null
      const name = this.form.organization_name || this.form.customer_name || `#${this.form.organization}`
      return { id: this.form.organization, name }
    },
    quotationPlaceholder () {
      if (this.form && this.form.parent) return this.$t('Inherited from parent task')
      if (!this.form || !this.form.organization) return this.$t('Select organization first')
      return this.$t('Type to search')
    },
    macroSelectValue () {
      if (!this.form.category) return null
      const text = this.form.category_name ||
        (this.form.category_detail && this.form.category_detail.name) ||
        this.macroNameLookup(this.form.category)
      return { value: this.form.category, text: text || `#${this.form.category}` }
    },
    prioritySelectValue () {
      if (!this.form.priority) return null
      const text = this.priorityNameLookup(this.form.priority) ||
        this.form.priority_name ||
        (this.form.priority_detail && this.form.priority_detail.name)
      const color = this.priorityColorLookup(this.form.priority) ||
        (this.form.priority_detail && this.form.priority_detail.color) ||
        '#6c757d'
      return { value: this.form.priority, text: text || `#${this.form.priority}`, color }
    },
    stateSelectValue () {
      if (!this.form.state) return null
      const match = this.taskStateEntries && this.taskStateEntries.find((i) => i.id === this.form.state)
      const text = (match && match.name) ||
        this.form.state_name ||
        (this.form.state_detail && this.form.state_detail.name) ||
        this.getStateDisplay(this.form.state)
      const color = (match && match.color) ||
        (this.form.state_detail && this.form.state_detail.color) ||
        '#6c757d'
      return { value: this.form.state, text: text || `#${this.form.state}`, color }
    },
    assigneesMulti () {
      if (!this.form.assignees) return []
      const items = this.ensureArray(this.form.assignees)
      if (items.length === 0) return []
      if (typeof items[0] === 'object') {
        return items.map((item) => ({
          id: item.id,
          name: (item.full_name || `${item.first_name || ''} ${item.last_name || ''}`.trim() || item.email || `#${item.id}`).toUpperCase()
        }))
      }
      return items.map((id) => {
        // Il nome dei già-selezionati va preso da assignees_detail (stabile), non
        // dalle opzioni: durante la ricerca queste vengono filtrate e chi non è nei
        // risultati perderebbe il nome mostrando "#id".
        const detail = this.ensureArray(this.form.assignees_detail).find((i) => i.id === id)
        if (detail) {
          return { id, name: (detail.full_name || `${detail.first_name || ''} ${detail.last_name || ''}`.trim() || detail.email || `#${id}`).toUpperCase() }
        }
        const u = this.assigneeOptionsMulti.find((opt) => opt.id === id)
        return { id, name: u ? u.name : `#${id}` }
      })
    },
    tagsMulti () {
      if (!this.form.tags) return []
      const items = this.ensureArray(this.form.tags)
      if (items.length === 0) return []
      if (typeof items[0] === 'object') {
        return items.map((item) => ({ id: item.id, name: item.name }))
      }
      return items.map((id) => {
        const t = this.tagOptionsMulti.find((opt) => opt.id === id)
        return { id, name: t ? t.name : `#${id}` }
      })
    },
    quotationAutocompleteValue () {
      const id = this.form.quotation
      if (id == null) return null
      const detail = this.form.quotation_detail
      if (detail && detail.id === id) {
        return { id, name: this.formatQuotationLabel(detail) }
      }
      const q = this.quotationOptionsMulti.find((opt) => opt.id === id)
      return { id, name: q ? q.name : `#${id}` }
    },
    terminateModalTask () {
      return {
        id: this.id,
        name: this.form.name,
        subject: this.form.name,
        code: this.form.code,
        quotation: this.form.quotation,
        quotation_detail: this.form.quotation_detail,
        quotation_row: this.form.quotation_row,
        income: this.form.income,
        parent: this.form.parent,
        state: this.form.state
      }
    }
  },
  methods: {
    ...mapActions({
      ...TASK_TYPES.GENERIC.task.task.DETAIL.ACTIONS,
      markDone: TASK_TYPES.GENERIC.task.task.DETAIL.ACTIONS.markDone
    }),
    initFields () {
      this.fetchTaskChoices()
      this.fetchMacroOptions()
      this.fetchPriorityOptions()
      this.fetchTagOptions()
      this.fetchAssigneeOptions()
      if (!this.hideRelations) {
        this.fetchQuotationOptions()
      }
    },
    init () {
      this.cleanErrors()
      const choicesReady = this.fetchTaskChoices()
      this.fetchMacroOptions()
      this.fetchPriorityOptions()
      this.fetchTagOptions()
      this.fetchAssigneeOptions()
      const isSubtaskCreation = !!(this.detailParams && this.detailParams.isSubtaskCreation)
      const willRetrieveDetail = this.id && !isSubtaskCreation
      // La form di edit/subtask viene popolata in modo asincrono (retrieve o
      // detailParams.item): in quei casi la fetch va rimandata a form pronta,
      // così il filtro per Azienda viene applicato.
      const deferQuotationFetch = willRetrieveDetail || isSubtaskCreation
      if (!this.hideRelations && !deferQuotationFetch) {
        this.fetchQuotationOptions()
      }
      if (this.id && !isSubtaskCreation) {
        this.retrieve({ id: this.id }).then(() => {
          this.makeFormReadonly()
          this.setForm()
          if (!this.hideRelations) {
            this.fetchQuotationOptions()
          }
        })
      } else if (isSubtaskCreation && this.detailParams.item) {
        Promise.resolve(choicesReady).then(() => {
          const form = Object.assign({}, this.detailParams.item)
          form.parent = form.id
          form.parent_name = form.name
          delete form.id
          form.assignees = (this.detailParams.defaultAssignees && this.detailParams.defaultAssignees.length)
            ? this.detailParams.defaultAssignees.slice()
            : []
          this.$set(this, 'form', form)
          if (form.due_date) {
            this.$set(this, 'dueDate', moment(new Date(form.due_date)))
          } else {
            this.$set(this, 'dueDate', null)
          }
          this.makeFormEditable()
          if (!this.hideRelations) {
            this.fetchQuotationOptions()
          }
        })
      } else {
        Promise.resolve(choicesReady).then(() => {
          const defaults = {
            state: this.getStateIdByCode('open'),
            assignees: [],
            tags: [],
            quotation: null,
            quotation_detail: null
          }
          if (this.editParams && this.editParams.organization) {
            defaults.organization = this.editParams.organization
            if (this.editParams.organizationName) {
              defaults.organization_name = this.editParams.organizationName
            }
          }
          if (this.editParams && this.editParams.parent) {
            defaults.parent = this.editParams.parent
          }
          if (this.editParams && this.editParams.name) {
            defaults.name = this.editParams.name
          }
          if (this.editParams && this.editParams.description) {
            defaults.description = this.editParams.description
          }
          this.$set(this, 'form', defaults)
          this.makeFormEditable()
          const hasOrg = !!(this.editParams && this.editParams.organization)
          if (!hasOrg && this.categoryTypeCode === 'company') {
            this.fetchOwnerOrganizationDefault().then((org) => {
              if (org) {
                this.$set(this.form, 'organization', org.id)
                this.$set(this.form, 'organization_name', org.name)
                this.fetchQuotationOptions()
              }
            })
          } else if (!hasOrg && this.categoryTypeCode === 'customer') {
            this.fetchCustomerOrganizationOptions().then((items) => {
              this.$set(this, 'organizationList', items || [])
            })
          }
        })
      }
    },
    setForm () {
      const form = Object.assign({}, this.detail)
      this.$set(this, 'form', form)
      if (form.due_date) {
        this.$set(this, 'dueDate', moment(new Date(form.due_date)))
      } else {
        this.$set(this, 'dueDate', null)
      }
    },
    onDueDateChange (evt) {
      const date = evt && evt.date
      if (date) {
        this.$set(this, 'dueDate', date)
        this.$set(this.form, 'due_date', date.format('YYYY-MM-DD'))
      } else {
        this.$set(this, 'dueDate', null)
        this.$set(this.form, 'due_date', null)
      }
    },
    onSelectMacro (item) { this.selectSingleToValue(item, 'category', 'category_name') },
    onClearMacro () { this.clearSingle('category', 'category_name') },
    onSelectPriority (item) { this.selectSingleToValue(item, 'priority', 'priority_name') },
    onClearPriority () { this.clearSingle('priority', 'priority_name') },
    onSelectState (item) { this.$set(this.form, 'state', item.value) },
    onClearState () { this.$set(this.form, 'state', null) },

    onSearchAssignee (value) { this.fetchAssigneeOptions(value) },
    onSelectAssignee (item) {
      const current = this.ensureArray(this.form.assignees).slice()
      if (!current.includes(item.id)) current.push(item.id)
      this.$set(this.form, 'assignees', current)
      const detail = this.ensureArray(this.form.assignees_detail).slice()
      if (!detail.find((i) => i.id === item.id)) detail.push({ id: item.id, full_name: item.name })
      this.$set(this.form, 'assignees_detail', detail)
    },
    onRemoveAssignee (item) {
      this.$set(this.form, 'assignees', this.ensureArray(this.form.assignees).filter((id) => id !== item.id))
      this.$set(this.form, 'assignees_detail', this.ensureArray(this.form.assignees_detail).filter((i) => i.id !== item.id))
    },
    onClearAssignees () {
      this.$set(this.form, 'assignees', [])
      this.$set(this.form, 'assignees_detail', [])
    },

    onSearchTag (value) { this.fetchTagOptions(value) },
    onAddTag (name) {
      const label = (name || '').trim()
      if (!label) return
      this.createTaskTagAction({ name: label, slug: slugify(label, { remove: /[*+~.()'"!:@]/g }) }).then((created) => {
        if (!created || created.id == null) return
        this.onSelectTag({ id: created.id, name: created.name })
        this.fetchTagOptions()
      })
    },
    onSelectTag (item) {
      const current = this.ensureArray(this.form.tags).slice()
      if (!current.includes(item.id)) current.push(item.id)
      this.$set(this.form, 'tags', current)
      const detail = this.ensureArray(this.form.tags_detail).slice()
      if (!detail.find((i) => i.id === item.id)) detail.push({ id: item.id, name: item.name })
      this.$set(this.form, 'tags_detail', detail)
    },
    onRemoveTag (item) {
      this.$set(this.form, 'tags', this.ensureArray(this.form.tags).filter((id) => id !== item.id))
      this.$set(this.form, 'tags_detail', this.ensureArray(this.form.tags_detail).filter((i) => i.id !== item.id))
    },
    onClearTags () {
      this.$set(this.form, 'tags', [])
      this.$set(this.form, 'tags_detail', [])
    },

    onSearchOrganization (value) {
      this.fetchCustomerOrganizationOptions(value).then((items) => {
        this.$set(this, 'organizationList', items || [])
      })
    },
    onSelectOrganization (item) {
      this.$set(this.form, 'organization', item.id)
      this.$set(this.form, 'organization_name', item.name)
      this.$set(this.form, 'quotation', null)
      this.$set(this.form, 'quotation_detail', null)
      this.fetchQuotationOptions()
    },
    onClearOrganization () {
      this.$set(this.form, 'organization', null)
      this.$set(this.form, 'organization_name', null)
      this.$set(this.form, 'quotation', null)
      this.$set(this.form, 'quotation_detail', null)
      this.$set(this, 'organizationList', [])
    },

    onSearchQuotation (value) { this.fetchQuotationOptions(value) },
    onSelectQuotation (item) {
      this.$set(this.form, 'quotation', item.id)
      const raw = this.quotationList && this.quotationList.results
        ? this.quotationList.results.find((q) => q.id === item.id)
        : null
      this.$set(this.form, 'quotation_detail', raw
        ? { id: item.id, quotation_code: raw.quotation_code, number: raw.number, description: raw.description, order_done: raw.order_done, customer_name: raw.customer_name || (raw.customer && raw.customer.name) || null }
        : { id: item.id, description: item.name })
    },
    onClearQuotation () {
      this.$set(this.form, 'quotation', null)
      this.$set(this.form, 'quotation_detail', null)
    },

    getFormData () {
      const data = Object.assign({}, this.form)
      data.assignees = this.multiselectIdList(this.ensureArray(data.assignees))
      data.tags = this.multiselectIdList(this.ensureArray(data.tags))
      if (this.hideRelations && this.editParams && this.editParams.quotationId && !data.quotation !== this.editParams.quotationId) {
        data.quotation = this.editParams.quotationId
      }
      if (this.editParams && this.editParams.quotationRowId) {
        data.quotation_row = this.editParams.quotationRowId
      }
      if (this.hideRelations && this.editParams && this.editParams.quotationOrderDone === false) {
        data.hidden = true
      }

      delete data.category_name
      delete data.priority_name
      delete data.category_detail
      delete data.priority_detail
      delete data.state_name
      delete data.assignees_detail
      delete data.tags_detail
      delete data.quotation_detail
      delete data.organization_name
      delete data.parent_name
      delete data.subtasks_count
      delete data.completed_at
      delete data.date_create
      delete data.date_last_modify
      delete data.creator
      delete data.last_modifier
      delete data.comments
      return data
    },
    macroNameLookup (id) {
      const match = this.macroOptions.find((o) => o.value === id)
      return match ? match.text : null
    },
    priorityNameLookup (id) {
      const match = this.taskPriorityOptions.find((o) => o.value === id)
      return match ? match.text : null
    },
    priorityColorLookup (id) {
      const match = this.taskPriorityOptions.find((o) => o.value === id)
      return match ? match.color : null
    },
    onSubmit () {
      const formData = this.getFormData()
      if (!this.validateSubtasks()) return
      if (this.hideRelations && !(this.editParams && this.editParams.quotationRowId)) {
        formData.subtasks = this.collectSubtasksData()
        this.$emit('task-pending', formData)
        this.closeForm()
        return
      }
      const promise = this.id ? this.update(formData) : this.create(formData)
      promise.then(
        (response) => {
          const savedTask = Object.assign({}, this.detail)
          const pending = this.subtasks.length
          this.createSubtasks(savedTask.id).then(() => {
            if (!pending) {
              this.afterSubmit(response)
              return
            }
            // La create delle sotto-attività ha sovrascritto `detail` con
            // l'ultimo figlio creato: ricarica il padre prima di tornare in
            // sola lettura, altrimenti la form mostrerebbe i dati del figlio.
            this.resetSubtasks()
            if (!savedTask.id) {
              this.afterSubmit(savedTask)
              return
            }
            this.retrieve({ id: savedTask.id }).then(() => {
              this.setForm()
              this.afterSubmit(this.detail)
            })
          })
        }
      )
    },
    validateSubtasks () {
      const refs = this.$refs.subtaskForms
      if (!refs) return true
      const list = Array.isArray(refs) ? refs : [refs]
      let ok = true
      list.forEach((ref) => { if (ref && !ref.validate()) ok = false })
      if (!ok) {
        this.$store.dispatch('notifyError', {
          error: { detail: this.$t('Some subtasks are incomplete') }
        })
      }
      return ok
    },
    collectSubtasksData () {
      const refs = this.$refs.subtaskForms
      if (!refs) return []
      const list = Array.isArray(refs) ? refs : [refs]
      return list.filter(Boolean).map((ref) => ref.getFormData())
    },
    createSubtasks (parentId) {
      if (!parentId || !this.subtasks.length) return Promise.resolve()
      const refs = this.$refs.subtaskForms
      if (!refs) return Promise.resolve()
      const list = Array.isArray(refs) ? refs : [refs]
      return Promise.allSettled(
        list.filter(Boolean).map((ref) => {
          const data = ref.getFormData()
          data.parent = parentId
          data.organization = this.form.organization
          const quotationId = this.editParams && this.editParams.quotationId
          if (quotationId) data.quotation = quotationId
          return this.create(data)
        })
      ).then((results) => {
        const failed = results.filter((r) => r.status === 'rejected').length
        if (failed) {
          this.$store.dispatch('notifyError', {
            error: { detail: this.$t('Some subtasks could not be created') + ` (${failed})` }
          })
        }
      })
    },
    addSubtask () {
      this.subtasks.push({ _key: `${Date.now()}-${Math.random().toString(36).slice(2)}` })
    },
    removeSubtask (index) {
      const removed = this.subtasks[index]
      this.subtasks.splice(index, 1)
      if (removed) this.$delete(this.subtaskValidity, removed._key)
    },
    onSubtaskValidity (key, valid) {
      this.$set(this.subtaskValidity, key, valid)
    },
    resetSubtasks () {
      this.$set(this, 'subtasks', [])
      this.$set(this, 'subtaskValidity', {})
    },
    makeFormEditable () {
      // Ogni ingresso in modifica riparte senza sotto-attività in bozza: quelle
      // di una sessione annullata non devono ricomparire.
      this.resetSubtasks()
      this.$set(this, 'formEditable', true)
    },
    afterSubmit (response) {
      setTimeout(() => {
        const onSaved = (this.editParams && this.editParams.onSaved) ||
          (this.detailParams && this.detailParams.onSaved)
        if (typeof onSaved === 'function') {
          onSaved(response || this.detail)
        }
        if (!this.id) {
          this.$emit('task-created', response || this.detail)
          this.closeForm()
        } else {
          this.makeFormReadonly()
        }
      }, 300)
    },
    notifyListReload () {
      // Una chiusura (mark-done / terminazione / chiusura sottoattività) NON passa
      // da afterSubmit, quindi la lista/albero dietro il dettaglio non verrebbe
      // ricaricata e il record chiuso resterebbe visibile (stale). Richiamiamo
      // esplicitamente l'onSaved della lista (getList per la dashboard,
      // reloadList per l'organizzazione).
      const onSaved = (this.detailParams && this.detailParams.onSaved) ||
        (this.editParams && this.editParams.onSaved)
      if (typeof onSaved === 'function') {
        onSaved(this.detail)
      }
    },
    onMarkDone () {
      if (this.detail && this.detail.has_open_subtasks) {
        this.$set(this, 'showOpenSubtasksModal', true)
        return
      }
      if (this.form && this.form.quotation) {
        this.$set(this, 'showTerminateModal', true)
        return
      }
      this.markDone({ id: this.id }).then(() => {
        this.setForm()
        // Let the parent list/dashboard reload the visible-task/summary/ counters.
        this.$emit('task-done', this.detail)
        this.notifyListReload()
      })
    },
    onSubtaskClosed () {
      // Una sotto-attività è stata chiusa dalla modale: lascia ai contatori
      // dashboard/summary del parent la possibilità di ricaricarsi.
      this.$emit('task-done', this.detail)
      this.notifyListReload()
    },
    onOpenSubtasksAllClosed () {
      // Tutti i figli di primo livello sono chiusi: ricarica il dettaglio del
      // padre e prosegue con la sua chiusura (ora salta il ramo
      // has_open_subtasks e va su preventivo/markDone).
      this.$set(this, 'showOpenSubtasksModal', false)
      if (!this.id) return
      this.retrieve({ id: this.id }).then(() => {
        this.setForm()
        this.onMarkDone()
      })
    },
    onTerminateModalClosed () {
      this.$set(this, 'showTerminateModal', false)
    },
    onTerminateModalDone () {
      this.$set(this, 'showTerminateModal', false)
      if (this.id) {
        this.retrieve({ id: this.id }).then(() => {
          this.setForm()
          this.$emit('task-done', this.detail)
          this.notifyListReload()
        })
      }
    }
  },
  validations: {
    form: {
      name: { required },
      category: { required },
      priority: { required },
      assignees: { required: (value) => Array.isArray(value) && value.length > 0 }
    }
  }
}
</script>

<style scoped>
.parent-box {
  border: 1px solid #e2e6ea;
  border-left: 3px solid #00acac;
  border-radius: 4px;
  background: #f9fafb;
  padding: 12px 16px;
}
.parent-box__head {
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 4px 12px;
  margin-bottom: 10px;
  padding-bottom: 8px;
  border-bottom: 1px solid #e9ecef;
}
.parent-box__eyebrow {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #00acac;
  white-space: nowrap;
}
.parent-box__title {
  font-size: 15px;
  font-weight: 600;
  color: #2d353c;
  word-break: break-word;
}
.parent-box__cell {
  padding: 6px 16px 6px 0;
}
.parent-box__label {
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #9aa3ab;
  margin-bottom: 2px;
}
.parent-box__value {
  font-size: 13px;
  line-height: 1.35;
  color: #2d353c;
  word-break: break-word;
}
.parent-box__value--desc {
  white-space: pre-line;
  color: #575f66;
}
</style>
